import { useState, useEffect, useRef } from 'react';

const testimonials = [
  {
    name: 'Ahmed Al-Rashidi',
    role: 'Procurement Manager',
    company: 'Gulf Construction LLC',
    country: 'UAE',
    flag: '🇦🇪',
    text: 'We have been sourcing H-beams and ERW pipes from Steel Link for 3 years. Their prices are always competitive and the mill certificates are always correct. Shipments arrive on time, every time.',
    rating: 5,
    product: 'H-Beam & ERW Pipe',
    orders: '18 orders',
  },
  {
    name: 'Emmanuel Okafor',
    role: 'Director',
    company: 'Okafor Steel Merchants',
    country: 'Nigeria',
    flag: '🇳🇬',
    text: 'I was nervous about my first order from China. Steel Link made it very easy. They explained everything — the L/C process, the shipping documents, the inspection. Now I order every month.',
    rating: 5,
    product: 'Rebar & Galvanized Pipe',
    orders: '24 orders',
  },
  {
    name: 'Rajesh Patel',
    role: 'Owner',
    company: 'Patel Building Materials',
    country: 'India',
    flag: '🇮🇳',
    text: 'Very professional team. They found us a better grade of cold-rolled coil than what we were getting locally, at a lower price. The SGS inspection report gave us full confidence.',
    rating: 5,
    product: 'Cold-Rolled Coil',
    orders: '11 orders',
  },
  {
    name: 'Carlos Mendoza',
    role: 'Supply Chain Manager',
    company: 'Constructora Mendoza',
    country: 'Mexico',
    flag: '🇲🇽',
    text: 'Fast response, honest pricing, and no surprises. We ordered corrugated roofing sheets and sandwich panels for a large warehouse project. Everything arrived exactly as specified.',
    rating: 5,
    product: 'Roofing Sheets & Panels',
    orders: '7 orders',
  },
  {
    name: 'Kwame Asante',
    role: 'CEO',
    company: 'Asante Infrastructure Ltd',
    country: 'Ghana',
    flag: '🇬🇭',
    text: 'Steel Link is our go-to supplier for all structural steel. Their team knows the products well and always recommends the right grade for our projects. Highly recommended.',
    rating: 5,
    product: 'Structural Steel',
    orders: '15 orders',
  },
  {
    name: 'Fatima Al-Zahra',
    role: 'Purchasing Officer',
    company: 'Saudi Build Co.',
    country: 'Saudi Arabia',
    flag: '🇸🇦',
    text: 'We needed API 5CT casing pipes urgently for an oil field project. Steel Link delivered within the agreed timeline with full documentation. Excellent service and quality.',
    rating: 5,
    product: 'Casing Pipe API 5CT',
    orders: '9 orders',
  },
];

function TestimonialCard({ t, index }: { t: typeof testimonials[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="bg-white rounded-2xl p-6 border border-gray-100 flex flex-col gap-4 transition-all duration-500 hover:-translate-y-1 cursor-default"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.5s ease ${index * 0.08}s, transform 0.5s ease ${index * 0.08}s`,
        boxShadow: 'none',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(10,35,66,0.25)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#f3f4f6'; }}
    >
      {/* Top row: stars + verified */}
      <div className="flex items-center justify-between">
        <div className="flex gap-0.5">
          {Array.from({ length: t.rating }).map((_, i) => (
            <i key={i} className="ri-star-fill text-sm" style={{ color: '#f59e0b' }} />
          ))}
        </div>
        <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(10,35,66,0.07)', color: '#0a2342' }}>
          <i className="ri-verified-badge-fill text-xs" style={{ color: '#0a2342' }} />
          Verified Buyer
        </span>
      </div>

      {/* Quote */}
      <p className="text-gray-600 text-sm leading-relaxed flex-1">
        &ldquo;{t.text}&rdquo;
      </p>

      {/* Product tag + order count */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: 'rgba(10,35,66,0.07)', color: '#0a2342' }}>
          <i className="ri-box-3-line mr-1" />{t.product}
        </span>
        <span className="text-xs text-gray-400 font-medium">
          <i className="ri-repeat-line mr-1" />{t.orders}
        </span>
      </div>

      {/* Author */}
      <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-sm flex-shrink-0"
          style={{ backgroundColor: '#0a2342' }}
        >
          {t.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900 truncate">{t.name}</div>
          <div className="text-xs text-gray-500 truncate">{t.role}, {t.company}</div>
        </div>
        <span className="text-xl flex-shrink-0" title={t.country}>{t.flag}</span>
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  return (
    <section className="py-20" style={{ backgroundColor: '#f8fafc' }}>
      <div className="max-w-[1420px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-black px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase" style={{ backgroundColor: '#e8f0fb', color: '#0a2342' }}>
            <i className="ri-chat-quote-line" />
            What Buyers Say
          </div>
          <h2 className="text-3xl md:text-4xl font-black mb-3" style={{ color: '#0a2342' }}>
            Trusted by Buyers Worldwide
          </h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto">
            Real feedback from verified buyers across Africa, the Middle East, Asia, and beyond.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <TestimonialCard key={t.name} t={t} index={i} />
          ))}
        </div>

        {/* Bottom trust bar */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 py-8 border-t border-gray-200">
          {[
            { val: '500+', label: 'Happy Buyers', icon: 'ri-user-smile-line' },
            { val: '85%', label: 'Repeat Order Rate', icon: 'ri-repeat-line' },
            { val: '50+', label: 'Countries', icon: 'ri-global-line' },
            { val: '4.9/5', label: 'Average Rating', icon: 'ri-star-fill' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-2" style={{ backgroundColor: '#e8f0fb' }}>
                <i className={`${s.icon} text-base`} style={{ color: '#0a2342' }} />
              </div>
              <div className="text-2xl font-black" style={{ color: '#0a2342' }}>{s.val}</div>
              <div className="text-xs text-gray-500 font-medium mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
